import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArchiveIcon,
  ArrowLeft,
  Download,
  FileSignature,
  Lock,
  Send,
  Upload,
} from "lucide-react";

import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ModuleTitleER } from "@/components/er/ModuleTitleER";
import {
  CATEGORY_LABELS,
  PRIORITY_LABELS,
  STATUS_LABELS,
  priorityPillClass,
  statusPillClass,
} from "./erLabels";

const NEXT_STATUSES = {
  new: ["acknowledged"],
  acknowledged: ["under_review", "resolved", "closed"],
  under_review: ["investigation", "resolved", "closed"],
  investigation: ["resolved", "closed"],
  resolved: ["closed"],
  closed: [],
};

export default function ErCaseDetail() {
  const { user } = useAuth();
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [reporter, setReporter] = useState(null);
  const [investigator, setInvestigator] = useState(null);
  const [campusName, setCampusName] = useState(null);
  const [messages, setMessages] = useState([]);
  const [notes, setNotes] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [noteText, setNoteText] = useState("");
  const [transitionNote, setTransitionNote] = useState("");
  const [poUsers, setPoUsers] = useState([]);
  const [selectedInvestigator, setSelectedInvestigator] = useState("");
  const [selectedNextStatus, setSelectedNextStatus] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");
  const [busy, setBusy] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");

  const load = async () => {
    try {
      const [{ data }, dir] = await Promise.all([
        api.get(`/er/cases/${caseId}`),
        api.get("/users/directory"),
      ]);
      setCaseData(data.case);
      setReporter(data.reporter);
      setInvestigator(data.investigator);
      setCampusName(data.campus_name);
      setMessages(data.messages || []);
      setNotes(data.notes || []);
      setTimeline(data.timeline || []);
      setAttachments(data.attachments || []);
      setSelectedInvestigator(data.case?.assigned_investigator_id || "");
      setSelectedPriority(data.case?.priority || "medium");
      setPoUsers(
        (dir.data || []).filter((u) => ["po_admin", "super_admin"].includes(u.role)),
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      navigate("/er/cases");
    }
  };

  useEffect(() => {
    load();
     
  }, [caseId]);

  const nextStatuses = useMemo(
    () => (caseData ? NEXT_STATUSES[caseData.status] || [] : []),
    [caseData],
  );

  const transition = async () => {
    if (!selectedNextStatus) return;
    setBusy(true);
    try {
      await api.post(`/er/cases/${caseId}/transition`, {
        new_status: selectedNextStatus,
        note: transitionNote.trim() || null,
      });
      toast.success("Status updated.");
      setSelectedNextStatus("");
      setTransitionNote("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setPriority = async () => {
    if (!selectedPriority || selectedPriority === caseData?.priority) return;
    setBusy(true);
    try {
      await api.post(`/er/cases/${caseId}/priority`, { priority: selectedPriority });
      toast.success("Priority updated.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const assign = async () => {
    setBusy(true);
    try {
      await api.post(`/er/cases/${caseId}/assign`, {
        investigator_user_id: selectedInvestigator || null,
      });
      toast.success("Assignment updated.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/er/cases/${caseId}/messages`, { body: replyText.trim() });
      setReplyText("");
      toast.success("Reply sent.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/er/cases/${caseId}/notes`, { body: noteText.trim() });
      setNoteText("");
      toast.success("Confidential note added.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/er/cases/${caseId}/attachments`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Attachment uploaded.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const downloadAttachment = async (a) => {
    try {
      const resp = await api.get(`/er/cases/${caseId}/attachments/${a.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: a.mime_type }));
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const archive = async () => {
    if (!archiveReason.trim()) {
      toast.error("Please add a reason.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/er/cases/${caseId}/archive`, { reason: archiveReason.trim() });
      toast.success("Case archived.");
      navigate("/er/cases");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!caseData) {
    return (
      <div className="patria-card p-8" data-testid="er-case-loading">
        <p className="text-charcoal-muted italic">Loading case…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="er-case-detail-page">
      <Link
        to="/er/cases"
        className="inline-flex items-center text-forest-700 text-sm font-medium"
      >
        <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
        Back to case queue
      </Link>

      <div>
        <ModuleTitleER />
        <div className="mt-3 flex flex-wrap items-baseline gap-3">
          <p className="text-xs uppercase tracking-widest text-charcoal-muted">
            {caseData.case_number}
          </p>
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs ${statusPillClass(caseData.status)}`}
            data-testid="er-case-status-pill"
          >
            {STATUS_LABELS[caseData.status] || caseData.status}
          </span>
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${priorityPillClass(caseData.priority)}`}
          >
            {PRIORITY_LABELS[caseData.priority]} priority
          </span>
          {caseData.reporter_type === "anonymous" && (
            <span className="inline-flex items-center rounded-full border border-terracotta-500/30 bg-terracotta-500/15 text-terracotta-600 px-2 py-0.5 text-xs">
              Anonymous reporter
            </span>
          )}
          {caseData.sla_overdue && (
            <span className="inline-flex items-center rounded-full bg-terracotta-500 text-cream-100 px-2 py-0.5 text-xs font-medium" data-testid="er-case-sla-overdue-pill">
              SLA overdue
            </span>
          )}
        </div>
        <h1 className="font-serif text-forest-900 text-4xl mt-2">
          {CATEGORY_LABELS[caseData.category] || caseData.category}
        </h1>
        <p className="text-sm text-charcoal-muted mt-2">
          Submitted {new Date(caseData.created_at).toLocaleString()}
          {campusName ? ` • ${campusName}` : ""}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="patria-card p-6">
            <h3 className="font-serif text-forest-900 text-xl">Report</h3>
            <p className="mt-3 text-charcoal whitespace-pre-line">{caseData.description}</p>
            {caseData.parties_involved && (
              <>
                <p className="mt-4 text-xs uppercase tracking-widest text-charcoal-muted">
                  Parties involved
                </p>
                <p className="mt-1 text-charcoal-soft">{caseData.parties_involved}</p>
              </>
            )}
            {reporter && (
              <>
                <p className="mt-4 text-xs uppercase tracking-widest text-charcoal-muted">Reporter</p>
                <p className="mt-1 text-charcoal font-medium">
                  {reporter.name}
                  {reporter.job_title ? ` • ${reporter.job_title}` : ""}
                </p>
                <p className="text-xs text-charcoal-muted">{reporter.email}</p>
              </>
            )}
          </section>

          <section className="patria-card p-6">
            <h3 className="font-serif text-forest-900 text-xl">Messages With Reporter</h3>
            <ul className="mt-4 space-y-3">
              {messages.length === 0 && (
                <li className="text-sm text-charcoal-muted italic">
                  No messages yet.
                </li>
              )}
              {messages.map((m) => (
                <li
                  key={m.id}
                  className={`patria-card p-4 ${m.sender_type === "po" ? "border-l-4 border-terracotta-500" : "border-l-4 border-forest-600"}`}
                  data-testid={`er-msg-${m.id}`}
                >
                  <p className="text-xs uppercase tracking-widest text-charcoal-muted">
                    {m.sender_type === "po" ? `${m.sender_name} • People Operations` : (m.sender_name || "Reporter")} —{" "}
                    {new Date(m.created_at).toLocaleString()}
                  </p>
                  <p className="mt-2 text-charcoal whitespace-pre-line">{m.body}</p>
                </li>
              ))}
            </ul>
            <div className="mt-5">
              <label className="patria-label">Reply as People Operations</label>
              <textarea
                className="patria-input mt-2"
                rows={4}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                data-testid="er-case-reply"
                placeholder="Warm, direct, action-oriented."
              />
              <div className="mt-3 flex justify-end">
                <button
                  onClick={sendReply}
                  disabled={!replyText.trim() || busy}
                  className="patria-btn-primary"
                  data-testid="er-case-send-reply"
                >
                  <Send className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                  Send reply
                </button>
              </div>
            </div>
          </section>

          <section
            className="patria-card p-6 bg-forest-600/[.03] border border-terracotta-500/30"
            data-testid="er-case-notes-panel"
          >
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-terracotta-500" strokeWidth={1.5} />
              <h3 className="font-serif text-forest-900 text-xl">
                Confidential Notes — People Operations Only
              </h3>
            </div>
            <p className="text-xs text-charcoal-muted mt-1">
              Reporters and respondents never see these notes.
            </p>
            <ul className="mt-4 space-y-3">
              {notes.length === 0 && (
                <li className="text-sm text-charcoal-muted italic">
                  No notes yet.
                </li>
              )}
              {notes.map((n) => (
                <li key={n.id} className="patria-card p-4" data-testid={`er-note-${n.id}`}>
                  <p className="text-xs uppercase tracking-widest text-charcoal-muted">
                    {n.author_name} — {new Date(n.created_at).toLocaleString()}
                    {n.updated_at !== n.created_at && " (edited)"}
                  </p>
                  <p className="mt-2 text-charcoal whitespace-pre-line">{n.body}</p>
                </li>
              ))}
            </ul>
            <div className="mt-5">
              <label className="patria-label">Add a confidential note</label>
              <textarea
                className="patria-input mt-2"
                rows={4}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                data-testid="er-case-note-input"
                placeholder="Facts. Observations. Next steps. Nothing that would embarrass the church if read aloud."
              />
              <div className="mt-3 flex justify-end">
                <button
                  onClick={addNote}
                  disabled={!noteText.trim() || busy}
                  className="patria-btn-primary"
                  data-testid="er-case-note-add"
                >
                  <FileSignature className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                  Add note
                </button>
              </div>
            </div>
          </section>

          <section className="patria-card p-6" data-testid="er-case-attachments-panel">
            <h3 className="font-serif text-forest-900 text-xl">Attachments</h3>
            <ul className="mt-3 space-y-2">
              {attachments.length === 0 && (
                <li className="text-sm text-charcoal-muted italic">No attachments.</li>
              )}
              {attachments.map((a) => (
                <li
                  key={a.id}
                  className="patria-card p-3 flex items-center justify-between"
                  data-testid={`er-attachment-${a.id}`}
                >
                  <div>
                    <p className="font-medium text-forest-900 text-sm">{a.filename}</p>
                    <p className="text-xs text-charcoal-muted">
                      {a.uploaded_by_name} • {new Date(a.uploaded_at).toLocaleString()}
                    </p>
                  </div>
                  <button
                    className="patria-btn-secondary text-sm py-2 px-3"
                    onClick={() => downloadAttachment(a)}
                  >
                    <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                    Download
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-4">
              <label
                className="patria-btn-secondary inline-flex items-center cursor-pointer"
                data-testid="er-case-attach"
              >
                <Upload className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                Add attachment
                <input type="file" onChange={uploadAttachment} className="hidden" />
              </label>
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <section className="patria-card p-6" data-testid="er-case-controls">
            <h3 className="font-serif text-forest-900 text-lg">Case Controls</h3>

            <div className="mt-4">
              <label className="patria-label">Assigned investigator</label>
              <select
                className="patria-input mt-2 text-sm"
                value={selectedInvestigator}
                onChange={(e) => setSelectedInvestigator(e.target.value)}
                data-testid="er-case-assign-select"
              >
                <option value="">Unassigned</option>
                {poUsers.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
              <button
                onClick={assign}
                disabled={busy}
                className="patria-btn-secondary mt-2 text-sm w-full"
                data-testid="er-case-assign-btn"
              >
                Update assignment
              </button>
              {investigator && (
                <p className="text-xs text-charcoal-muted mt-2">
                  Currently assigned to <b className="text-forest-900">{investigator.name}</b>.
                </p>
              )}
            </div>

            <div className="mt-5">
              <label className="patria-label">Priority</label>
              <select
                className="patria-input mt-2 text-sm"
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                data-testid="er-case-priority-select"
              >
                {Object.entries(PRIORITY_LABELS).map(([k, l]) => (
                  <option key={k} value={k}>{l}</option>
                ))}
              </select>
              <button
                onClick={setPriority}
                disabled={busy || selectedPriority === caseData.priority}
                className="patria-btn-secondary mt-2 text-sm w-full"
              >
                Update priority
              </button>
            </div>

            {nextStatuses.length > 0 && (
              <div className="mt-5">
                <label className="patria-label">Change status</label>
                <select
                  className="patria-input mt-2 text-sm"
                  value={selectedNextStatus}
                  onChange={(e) => setSelectedNextStatus(e.target.value)}
                  data-testid="er-case-status-select"
                >
                  <option value="">Choose next status…</option>
                  {nextStatuses.map((s) => (
                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                  ))}
                </select>
                <textarea
                  className="patria-input mt-2 text-sm"
                  rows={2}
                  value={transitionNote}
                  onChange={(e) => setTransitionNote(e.target.value)}
                  placeholder="Optional note on this transition…"
                />
                <button
                  onClick={transition}
                  disabled={busy || !selectedNextStatus}
                  className="patria-btn-primary mt-2 text-sm w-full"
                  data-testid="er-case-status-apply"
                >
                  Apply status change
                </button>
              </div>
            )}

            {user?.role === "super_admin" || user?.role === "po_admin" ? (
              <div className="mt-5 pt-5 border-t border-sage-300/60">
                <label className="patria-label flex items-center gap-2">
                  <ArchiveIcon className="h-4 w-4 text-charcoal-muted" strokeWidth={1.5} />
                  Archive case (soft)
                </label>
                <textarea
                  className="patria-input mt-2 text-sm"
                  rows={2}
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  placeholder="Reason for archiving"
                  data-testid="er-case-archive-reason"
                />
                <button
                  onClick={archive}
                  disabled={busy || !archiveReason.trim()}
                  className="patria-btn-secondary mt-2 text-sm w-full"
                  data-testid="er-case-archive-btn"
                >
                  Archive
                </button>
              </div>
            ) : null}
          </section>

          <section className="patria-card p-6" data-testid="er-case-timeline-panel">
            <h3 className="font-serif text-forest-900 text-lg">Action Timeline</h3>
            <ol className="mt-4 space-y-3">
              {timeline.map((e) => (
                <li key={e.id} className="text-sm border-l-2 border-forest-600/25 pl-3">
                  <p className="text-xs text-charcoal-muted">
                    {new Date(e.created_at).toLocaleString()}
                  </p>
                  <p className="text-charcoal">
                    <b className="text-forest-900">{e.actor_name}</b> — {e.body}
                  </p>
                </li>
              ))}
              {timeline.length === 0 && (
                <li className="text-sm text-charcoal-muted italic">
                  No events yet.
                </li>
              )}
            </ol>
          </section>

          {caseData.reporter_type === "anonymous" && (
            <section className="patria-card p-4 border-l-4 border-terracotta-500 flex items-start gap-3">
              <AlertTriangle className="h-4 w-4 text-terracotta-500 mt-0.5" strokeWidth={1.5} />
              <p className="text-xs text-charcoal-soft">
                This report is anonymous. Reporter identity is never available — the reporter may
                only be reached by replying to this case; they will see it through the token
                portal.
              </p>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
